import type { SvgIconComponent } from '@mui/icons-material';
import {
  AssignmentInd as AssignmentIndIcon,
  EventNote as EventNoteIcon,
  FactCheck as FactCheckIcon,
  Groups as GroupsIcon,
  LocalShipping as LocalShippingIcon,
  Receipt as ReceiptIcon,
  ReportProblem as ReportProblemIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

/**
 * The `/reports` catalog — the single source of truth for which reports exist
 * and who may see them (GP-1327).
 *
 * Before this existed the hub hard-coded its cards and gated the whole tail of
 * the list on one `viewReports` boolean, so a report could not be delegated on
 * its own: granting Logistics the Delivery Method Report would have handed them
 * Pre-Bill and Kroll Reconciliation (billed liability) too. Visibility is now
 * evaluated per report, and both the hub grid and the sidebar entry read this
 * one array — add a report here and both surfaces pick it up.
 *
 * Server-side enforcement is separate and mandatory: hiding a card leaves the
 * report reachable by URL. Each entry's `permissions` mirror the
 * `@RequirePermission(...)` on the report's own backend routes.
 */

export type ReportPaletteColor =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';

export interface ReportDefinition {
  /** Stable id — used as the React key and in tests. */
  key: string;
  title: string;
  description: string;
  path: string;
  icon: SvgIconComponent;
  /** Theme palette entry used for the card's icon tile / hover border. */
  color: ReportPaletteColor;
  /**
   * Custom permissions that grant this report. OR semantics — holding any one
   * of them is enough, matching `CustomPermissionsGuard` on the backend.
   */
  permissions: string[];
  /**
   * PRX-136: AND instead of OR — every one of `permissions` is needed, matching
   * `StrictPathPharmacyAllPermissionsGuard` on the backend route.
   */
  requiresAll?: boolean;
  /**
   * Internal reports: visible ONLY to these custom roles, with no null-role
   * fallback. A user whose role hasn't resolved must not see them.
   */
  restrictedToRoles?: string[];
  /** PRX-44: pharmacy-level module this report belongs to; hidden when the pharmacy switched it off. */
  module?: 'clinic';
  /**
   * PRX-136: a pharmacy visibility block (`visibility/visibilityCatalogue.ts`)
   * that must be shown for this report to appear. A pharmacy-level switch, so
   * it applies to every role, including a user with no role row.
   */
  visibilityKey?: string;
}

export const REPORT_CATALOG: ReportDefinition[] = [
  {
    key: 'pre-bill',
    title: 'Pre-Bill Report',
    description: 'View orders created before billing cutoff dates with total liability',
    path: '/reports/pre-bill',
    icon: ReceiptIcon,
    color: 'primary',
    permissions: ['viewReports'],
  },
  {
    key: 'iv-event-audit',
    title: 'IV Event Audit',
    description: 'View and audit NEXI IV event history, delivery changes, and order conversions',
    path: '/reports/iv-event-audit',
    icon: EventNoteIcon,
    color: 'info',
    permissions: ['viewReports'],
    module: 'clinic',
  },
  {
    key: 'delivery-method',
    title: 'Delivery Method Report',
    description:
      'Reclassify delivery type on completed orders for finance P&L allocation (retail vs specialty)',
    path: '/reports/delivery-method',
    icon: LocalShippingIcon,
    color: 'success',
    // Per-report grant: Logistics holds this and nothing else (GP-1327).
    permissions: ['viewDeliveryMethodReport'],
  },
  {
    key: 'management-performance',
    title: 'Management Performance',
    description: 'System speed, connectivity health, and order throughput KPIs for management',
    path: '/reports/management-performance',
    icon: SpeedIcon,
    color: 'warning',
    permissions: ['viewReports'],
  },
  {
    key: 'kroll-reconciliation',
    title: 'Kroll Reconciliation',
    description: 'Kroll fills with no PHOX order — billed liability with reversal-window urgency',
    path: '/reports/kroll-reconciliation',
    icon: FactCheckIcon,
    color: 'error',
    permissions: ['viewReports'],
  },
  {
    key: 'verification-productivity',
    title: 'Verification Productivity',
    description:
      'Prescriptions checked/verified per pharmacist and technician, filterable by day, month, or date range',
    path: '/reports/verification-productivity',
    icon: AssignmentIndIcon,
    color: 'secondary',
    // Mirrors @RequirePermission('viewReports') on UserProductivityController.
    permissions: ['viewReports'],
  },
  {
    key: 'drug-recall',
    title: 'Drug Recall',
    description:
      'Lots, expiries and dispense dates — find every patient who received a recalled lot',
    path: '/reports/drug-recall',
    icon: ReportProblemIcon,
    color: 'error',
    permissions: ['viewReports'],
  },
  {
    key: 'improvement',
    title: 'Improvement & Speed',
    description:
      'Operating metrics since go-live — queue speed, compliance, scale, per-site maturation, roadmap (internal)',
    path: '/reports/improvement',
    icon: TrendingUpIcon,
    color: 'primary',
    permissions: [],
    restrictedToRoles: ['wally_dev_admin'],
  },
  {
    key: 'patient-cohorts',
    title: 'Patient reports',
    description:
      'Blister pack, VIP, specialty drug, past due and profession lists of patients, with CSV export',
    path: '/reports/patient-cohorts',
    icon: GroupsIcon,
    color: 'info',
    // Mirrors PatientReportsController (PRX-136, security review S1):
    // StrictPathPharmacyAllPermissionsGuard + @RequirePermission('viewPatients', 'viewReports').
    permissions: ['viewPatients', 'viewReports'],
    requiresAll: true,
    visibilityKey: 'reports.patient_cohorts',
  },
];

export interface ReportAccessContext {
  /** Resolved custom role, or null when the user has none assigned. */
  customRole: { customRole: string } | null;
  hasCustomPermission: (permission: string) => boolean;
  hasCustomRole: (role: string) => boolean;
  /** PRX-44: false only when the pharmacy switched the Clinic / IV module off. */
  clinicModuleEnabled: boolean;
  /**
   * PRX-136: whether the pharmacy hides a visibility block (`useBlocksHidden`).
   * Absent means unresolved, and a report gated on a block stays hidden.
   */
  isBlockHidden?: (key: string) => boolean;
}

/**
 * Whether a report is visible to the current user.
 *
 * Callers MUST wait for the role to resolve before using this — a null
 * `customRole` means "no role assigned" (show everything, the project-wide
 * no-lockout fallback) and is indistinguishable from "still loading", which
 * is what made the hub flash reports the user wasn't entitled to.
 */
export const canSeeReport = (report: ReportDefinition, ctx: ReportAccessContext): boolean => {
  if (report.module === 'clinic' && !ctx.clinicModuleEnabled) return false;
  if (report.visibilityKey && (!ctx.isBlockHidden || ctx.isBlockHidden(report.visibilityKey))) return false;
  if (report.restrictedToRoles) {
    return report.restrictedToRoles.some((role) => ctx.hasCustomRole(role));
  }
  if (!ctx.customRole) return true;
  if (report.requiresAll) return report.permissions.every((permission) => ctx.hasCustomPermission(permission));
  return report.permissions.some((permission) => ctx.hasCustomPermission(permission));
};

export const getVisibleReports = (ctx: ReportAccessContext): ReportDefinition[] =>
  REPORT_CATALOG.filter((report) => canSeeReport(report, ctx));

/**
 * Every permission that grants at least one report — the gate for the sidebar's
 * Reports entry, so anyone with any report grant gets a discoverable way in.
 *
 * PRX-136: a report behind a visibility block does not add its permission
 * here. The patient reports need only `viewPatients`, which drivers and
 * assistants hold; counting it would give every one of them a Reports entry
 * that opens an empty hub at a pharmacy where the block is hidden.
 */
export const REPORT_ACCESS_PERMISSIONS: string[] = Array.from(
  new Set(REPORT_CATALOG.filter((report) => !report.visibilityKey).flatMap((report) => report.permissions)),
);

/** The visibility blocks the catalogue gates on, for one `useBlocksHidden` call. */
export const REPORT_VISIBILITY_KEYS: string[] = REPORT_CATALOG.flatMap((report) =>
  report.visibilityKey ? [report.visibilityKey] : [],
);
